import React, { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ISelectOption, Select } from 'components/forms/Selects'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ContactDisplayTile } from 'apps/fleet-planner/components/atoms/ContactDisplayTile'
import { CustomerContactForm } from 'apps/fleet-planner/components/forms/CustomerContactForm'
import { EditCustomerForm } from 'apps/fleet-planner/components/forms/EditCustomerForm'
import { useCustomerApi, useCustomerContactApi } from 'apps/fleet-planner/hooks'
import { ContactWithNotesSchemaType } from 'apps/fleet-planner/schema/ContactWithNotesSchema'
import * as Types from 'apps/fleet-planner/types'
import {
  createCustomerContactSuccess,
  updateCustomerSuccess,
} from 'apps/fleet-planner/utils/actions/customers'
import { customersListSelector } from 'apps/fleet-planner/utils/selectors'
import formatPhone from 'utils/formatPhone'

const addCustomerContactOption = { label: 'Add New Customer Contact', value: -1 }

interface IGetCustomerContactLabelArgs {
  name?: string | null
  email?: string
  phone?: string
}

/**
 * Returns formatted string for <Select /> options
 */
export const getCustomerContactLabel = ({
  email = '',
  name = '',
  phone = '',
}: IGetCustomerContactLabelArgs): string => {
  if (name) return name

  const formattedPhone = formatPhone(phone, {
    removeCountryCode: '+1',
  })
  const backupLabel = email || formattedPhone

  return `No Contact Name: ${backupLabel}`
}

/**
 * Returns array of options for <Select />
 */
const getCustomerContactOptions = (
  cust: Partial<Types.ICustomer> | undefined
): ISelectOption<number>[] => {
  const { contacts = [] } = cust || {}
  const options = contacts.map(({ email = '', id: value = 0, name = '', phone = '' }) => ({
    label: getCustomerContactLabel({ name, phone, email }),
    value,
  }))

  return [...options, addCustomerContactOption]
}

/**
 * Uses sourceContactId if available, otherwise uses id
 */
const getContactOption = (
  contact: Types.ICustomerContact | undefined
): ISelectOption<number> | undefined => {
  if (!contact || !contact.id) return addCustomerContactOption
  const { email = '', id = 0, name = '', phone = '', sourceContactId = 0 } = contact || {}
  return {
    label: getCustomerContactLabel({ name, phone, email }),
    value: (sourceContactId as number) ?? id,
  }
}

// The fields we expect to edit in this component
type EditingFields =
  | 'customerContactId'
  | 'customerContact'
  | 'customerNotes'
  | 'customer'
  | 'customerId'

interface ICustomerContactProps {
  customer?: Types.ICustomer
  customerContact?: Types.ICustomerContact
  onChange: (value: Partial<Pick<Types.ILoad, EditingFields>>) => void
  onCancel: () => void
}

export const CustomerContact = memo<ICustomerContactProps>(
  ({
    customer: customerProp,
    customerContact: customerContactProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
  }) => {
    const dispatch = useDispatch()
    const customers = useSelector(customersListSelector)
    const { isLoading: isCustomerApiLoading, updateCustomer } = useCustomerApi(undefined, false)
    const {
      createNewCustomerContact,
      error: apiError = '',
      isLoading: isCustomerContactApiLoading,
    } = useCustomerContactApi()

    // Create options array for <Select />
    const selectedCustomer = customers?.find(c => c.id === customerProp?.id)
    const contactOptions = useMemo(() => getCustomerContactOptions(selectedCustomer), [
      selectedCustomer,
    ])

    const [customerContactOption, setCustomerContactOption] = useState<
      ISelectOption<number> | undefined
    >(getContactOption(customerContactProp))

    // Update local state when props change
    useEffect(() => {
      setCustomerContactOption(getContactOption(customerContactProp))
    }, [customerContactProp])

    /**
     * Handles update changes to `notes` value for customer
     * from <EditCustomerForm />
     */
    const updateCustomerHandler = async (
      change: Partial<Pick<Types.ICustomer, 'contacts' | 'notes' | 'id'>> | undefined
    ) => {
      const { notes = '' } = change || {}
      if (!customerProp?.id) return undefined

      const response = await updateCustomer(customerProp?.id, {
        ...customerProp,
        notes,
      })

      // Update redux with this updated customer value
      if (response) dispatch(updateCustomerSuccess(response))

      return response
    }

    /**
     * Any updates from <EditCustomerForm />
     * @param {'contacts' | 'notes' | 'id'} change - any changes from form
     */
    const editFormChangeHandler = async (
      change: Partial<Pick<Types.ICustomer, 'contacts' | 'notes' | 'id'>> | undefined
    ) => {
      const res = await updateCustomerHandler(change)

      const { notes } = res || {}
      const { contacts = [] } = res || {}

      // Select canonical customer contact object
      const customerContact = contacts.find(c => c.id === customerContactOption?.value)

      // Use latest values to update parent component
      onChangeProp({
        customerNotes: notes ?? '',
        customer: customerProp,
        customerId: customerProp?.id,
        customerContact,
        customerContactId: customerContact?.id,
      })
    }

    /**
     * Any updates from <CustomerContactForm />
     * @param {ContactWithNotesSchemaType} data - Data from form output
     */
    const createFormChangeHandler = async (data: ContactWithNotesSchemaType) => {
      const { contact, notes } = data || {}
      const { email, name, phone, phoneExt } = contact || {}

      const update: Partial<Pick<Types.ILoad, EditingFields>> = {
        customerNotes: notes ?? '',
        customer: customerProp,
        customerId: customerProp?.id,
        customerContact: customerContactProp,
        customerContactId: customerContactProp?.id,
      }

      if ((name || email || phone || phoneExt) && customerProp?.id) {
        const response = await createNewCustomerContact(customerProp?.id, {
          ...contact,
          purpose: Types.CUSTOMER_CONTACT_PURPOSE.PRIMARY,
        })

        // If this fails, don't continue
        if (!response) return

        update.customerContact = response
        update.customerContactId = response?.id

        const { contacts = [] } = selectedCustomer || {}

        // Update parent customer object with latest contacts
        // customerId remains the same
        update.customer = {
          ...customerProp,
          contacts: [...contacts.filter(c => c.id !== response?.id), response!],
        }

        // Update Redux
        if (response) dispatch(createCustomerContactSuccess(response))
      }

      onChangeProp(update)
    }

    const cancelHandler = () => onCancelProp()
    const selectOptionHandler = (option: ISelectOption<number>) => setCustomerContactOption(option)

    const selectedContact = selectedCustomer?.contacts?.find(
      c => c.id === customerContactOption?.value
    )
    const isEditingCustomer = customerContactOption?.value && customerContactOption?.value !== -1
    const isAddingCustomer = !customerContactOption?.value || customerContactOption?.value === -1

    return (
      <>
        <div className='mb4'>
          Changes to this customer will only affect this load and loads you create later.
        </div>
        <div className='body-medium b mb4'>{customerProp?.name || 'No customer name'}</div>
        {apiError && (
          <div className='mb4'>
            <InlineMessage message={apiError} type={INLINE_MESSAGE_VARIANTS.ALERT} />
          </div>
        )}
        <Select
          large
          className='mb4'
          label='Customer Contact'
          options={contactOptions}
          onChange={selectOptionHandler}
          value={customerContactOption?.value || addCustomerContactOption.value}
          dataTest='customer-contact-select'
        />
        {isEditingCustomer && (
          <>
            <ContactDisplayTile contact={selectedContact} />
            <EditCustomerForm
              defaultValues={{ notes: customerProp?.notes ?? '' }}
              onSuccess={editFormChangeHandler}
              onCancel={cancelHandler}
              isLoading={isCustomerApiLoading}
            />
          </>
        )}
        {isAddingCustomer && (
          <CustomerContactForm
            onSuccess={createFormChangeHandler}
            onCancel={cancelHandler}
            isLoading={isCustomerContactApiLoading}
            defaultValues={{
              notes: customerProp?.notes ?? '',
            }}
          />
        )}
      </>
    )
  }
)
